import React, { useEffect, useState } from 'react';
import { World } from '../types';
import { Icon } from './ui/Icon';
import { fmtDateTime, shutdownReasonLabel, statusInfo, timeUntil } from '../utils/format';

interface Props {
  world: World;
  /** Short "12 min" form with the detail in a tooltip, for table rows. */
  compact?: boolean;
}

const TICK_MS = 30000;
const URGENT_MS = 5 * 60 * 1000;

/**
 * "Shuts down in 12 min · no players" for a running world whose plugin has an automatic shutdown armed.
 * The deadline comes from the server; only the label is recomputed locally every 30 s.
 * A running world whose admin override is on shows "Auto-shutdown off" instead (the flag is only sent
 * to admins / developers, so others see nothing).
 */
export const ShutdownCountdown: React.FC<Props> = ({ world, compact }) => {
  const [, setTick] = useState(0);
  const running = statusInfo(world.status).active;
  const active = running && !!world.shutdownReason;
  useEffect(() => {
    if (!active || !world.shutdownAt) return;
    const t = window.setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => window.clearInterval(t);
  }, [active, world.shutdownAt]);

  if (running && world.autoShutdownDisabled) {
    if (compact) return null;
    return (
      <span className="pill" title="Automatic shutdown was turned off for this world by an admin (Admin tab)">
        <Icon name="shield" size={12} /> Auto-shutdown off
      </span>
    );
  }

  if (!active) return null;

  if (world.shutdownReason === 'BYPASS') {
    if (compact) return null;
    return (
      <span className="pill" title="A staff owner with the shutdown bypass is online">
        <Icon name="shield" size={12} /> Auto-shutdown off
      </span>
    );
  }

  const left = timeUntil(world.shutdownAt);
  if (!left) return null;
  const urgent = new Date(world.shutdownAt as string).getTime() - Date.now() < URGENT_MS;
  const reason = shutdownReasonLabel(world.shutdownReason);
  const title = `Shuts down at ${fmtDateTime(world.shutdownAt)} unless someone joins (${reason})`;

  if (compact) {
    return (
      <span className={`chip ${urgent ? 'chip--danger' : ''}`} title={title} style={{ whiteSpace: 'nowrap' }}>
        <Icon name="clock" size={10} /> {left}
      </span>
    );
  }
  return (
    <span className={`pill ${urgent ? 'pill--error' : 'pill--warn'}`} title={title}>
      <Icon name="clock" size={12} /> Shuts down in {left} · {reason}
    </span>
  );
};

export default ShutdownCountdown;
